import type { Schemas } from '#shopware';

export type BatchProductSearchResult = Map<string, Schemas['ProductDetailResponse']>;

export default function useBatchProductSearch() {
    const { apiClient } = useShopwareContext();

    const search = async (productIds: string[]): Promise<BatchProductSearchResult> => {
        if (!productIds.length) {
            return new Map();
        }

        const uniqueIds = [...new Set(productIds)];

        const response = await apiClient.invoke('readProduct post /product', {
            headers: {
                'sw-include-seo-urls': 'true',
            } as any,
            body: {
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'id',
                        value: uniqueIds.join('|'),
                    },
                ],
                limit: uniqueIds.length,
                associations: {
                    mainCategories: {},
                    media: {},
                    manufacturer: {
                        associations: {
                            media: {},
                        },
                    },
                    categories: {},
                    seoUrls: {},
                    options: {
                        associations: {
                            group: {},
                        },
                    },
                },
            },
        });

        const result: BatchProductSearchResult = new Map();

        for (const product of response.data.elements || []) {
            result.set(product.id, {
                product,
                configurator: product.options
                    ? groupOptionsByGroup(product.options)
                    : [],
            } as Schemas['ProductDetailResponse']);
        }

        return result;
    };

    return { search };
}

export function groupOptionsByGroup(options: Schemas['PropertyGroupOption'][]): Schemas['PropertyGroup'][] {
    const groupMap = new Map<string, Schemas['PropertyGroup']>();

    for (const option of options) {
        const group = option.group;
        if (!group) continue;

        if (!groupMap.has(group.id)) {
            groupMap.set(group.id, {
                ...group,
                options: [],
            });
        }

        groupMap.get(group.id)!.options!.push(option);
    }

    return Array.from(groupMap.values());
}
